<template>
  <div class="user-center-page">
    <!-- 左侧导航栏 -->
    <AppSidebar />

    <div class="user-center-container">
      <div class="user-center-content">
        <div v-if="loading" class="loading">
          <div class="loading-spinner"></div>
          <div class="loading-text">加载中...</div>
        </div>

        <div v-else-if="error" class="error-container">
          <div class="error-icon">!</div>
          <div class="error-message">{{ error }}</div>
          <button class="retry-button" @click="fetchUserInfo">重试</button>
        </div>

        <div v-else-if="userInfo" class="user-profile">
          <!-- 用户基本信息卡片 -->
          <div class="profile-card">
            <div class="profile-header">
              <div class="profile-avatar">
                <img :src="userInfo.avatar" :alt="userInfo.nickname" />
              </div>
              <div class="profile-info">
                <h1 class="profile-name">{{ userInfo.nickname }}</h1>
                <div class="profile-meta">
                  <span class="profile-username">账号: {{ userInfo.username }}</span>
                  <span class="profile-id">ID: {{ userInfo.id }}</span>
                  <span class="profile-status">状态: {{ userInfo.status }}</span>
                </div>
                <div class="profile-bio" v-if="userInfo.bio">{{ userInfo.bio }}</div>
                <div class="profile-bio" v-else>这个人很懒，什么都没有留下~</div>
              </div>
              <div class="vip-badge" v-if="userInfo.is_vip">
                <span class="vip-icon">VIP</span>
              </div>
            </div>
          </div>

          <!-- 用户详细信息区域 -->
          <div class="user-details">
            <div class="details-section contact-info">
              <h2 class="section-title">联系方式</h2>
              <div class="info-item">
                <span class="info-label">邮箱:</span>
                <span class="info-value">{{ userInfo.email || '未设置' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">手机:</span>
                <span class="info-value">{{ userInfo.mobile || '未设置' }}</span>
              </div>
            </div>

            <div class="details-section account-info">
              <h2 class="section-title">账户信息</h2>
              <div class="info-item">
                <span class="info-label">积分:</span>
                <span class="info-value">{{ userInfo.score }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">金币:</span>
                <span class="info-value">{{ userInfo.coins }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">连续登录:</span>
                <span class="info-value">{{ userInfo.successions }} 天</span>
              </div>
              <div class="info-item">
                <span class="info-label">最长连续登录:</span>
                <span class="info-value">{{ userInfo.maxsuccessions }} 天</span>
              </div>
            </div>

            <div class="details-section vip-info" v-if="userInfo.is_vip">
              <h2 class="section-title">VIP信息</h2>
              <div class="vip-details">
                <div class="info-item" v-for="(value, key) in userInfo.vip_info" :key="key">
                  <span class="info-label">{{ formatVipInfoKey(key) }}:</span>
                  <span class="info-value">{{ formatVipInfoValue(key, value) }}</span>
                </div>
              </div>
            </div>

            <div class="details-section login-info">
              <h2 class="section-title">登录信息</h2>
              <div class="info-item">
                <span class="info-label">注册时间:</span>
                <span class="info-value">{{ formatDate(userInfo.jointime) }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">注册IP:</span>
                <span class="info-value">{{ userInfo.joinip }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">上次登录:</span>
                <span class="info-value">{{ formatDate(userInfo.prevtime) }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">本次登录:</span>
                <span class="info-value">{{ formatDate(userInfo.logintime) }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">登录IP:</span>
                <span class="info-value">{{ userInfo.loginip }}</span>
              </div>
            </div>
          </div>

          <!-- 操作按钮区域 -->
          <div class="action-buttons">
            <button class="btn-logout" @click="handleLogout">退出登录</button>
          </div>
        </div>
      </div>

      <!-- 页脚 -->
      <AppFooter />
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { getUserInfo, logout } from '@/api/user';
import AppSidebar from '@/components/layout/Sidebar.vue';
import AppFooter from '@/components/layout/Footer.vue';

export default {
  name: 'UserCenter',
  components: {
    AppSidebar,
    AppFooter
  },
  setup() {
    const router = useRouter();
    const userInfo = ref(null);
    const loading = ref(true);
    const error = ref(null);

    const fetchUserInfo = async () => {
      loading.value = true;
      error.value = null;
      
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('未登录，请先登录');
        }
        
        const data = await getUserInfo(token);
        if (!data) {
          throw new Error('获取用户信息失败');
        }
        
        userInfo.value = data;
        // 保存用户信息到本地存储
        localStorage.setItem('userInfo', JSON.stringify(data));
      } catch (err) {
        console.error('获取用户信息出错:', err);
        error.value = err.message || '获取用户信息失败，请稍后再试';
        
        // 如果是未登录错误，跳转到登录页
        if (err.message.includes('未登录')) {
          setTimeout(() => {
            router.push('/login');
          }, 1500);
        }
      } finally {
        loading.value = false;
      }
    };

    const formatDate = (timestamp) => {
      if (!timestamp) return '未知';
      
      const date = new Date(timestamp * 1000);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    };

    const formatVipInfoKey = (key) => {
      const keyMap = {
        level: 'VIP等级',
        expire_time: '到期时间',
        days_left: '剩余天数'
      };
      return keyMap[key] || key;
    };

    const formatVipInfoValue = (key, value) => {
      if (key === 'expire_time') {
        return formatDate(value);
      }
      return value;
    };

    const handleLogout = () => {
      logout();
      router.push('/login');
    };

    onMounted(() => {
      fetchUserInfo();
    });

    return {
      userInfo,
      loading,
      error,
      fetchUserInfo,
      formatDate,
      formatVipInfoKey,
      formatVipInfoValue,
      handleLogout
    };
  }
};
</script>

<style scoped>
.user-center-page {
  display: flex;
  min-height: 100vh;
}

.user-center-container {
  margin-left: 200px;
  width: calc(100% - 200px);
  padding: 20px;
}

.user-center-content {
  max-width: 1200px;
  margin: 0 auto;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #ff6b81;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  font-size: 16px;
  color: #666;
}

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
}

.error-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #ff6b81;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  font-weight: bold;
  margin-bottom: 20px;
}

.error-message {
  font-size: 16px;
  color: #666;
  margin-bottom: 20px;
}

.retry-button {
  padding: 8px 20px;
  background-color: #ff6b81;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.user-profile {
  margin-top: 20px;
}

.profile-card {
  background-color: white;
  border-radius: 10px;
  padding: 30px;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.05);
  margin-bottom: 30px;
}

.profile-header {
  display: flex;
  position: relative;
}

.profile-avatar {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 30px;
  border: 3px solid #ff6b81;
}

.profile-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-info {
  flex: 1;
}

.profile-name {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 10px;
  color: #333;
}

.profile-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 15px;
}

.profile-username, .profile-id, .profile-status {
  font-size: 14px;
  color: #666;
}

.profile-bio {
  font-size: 14px;
  color: #666;
  line-height: 1.5;
}

.vip-badge {
  position: absolute;
  top: 0;
  right: 0;
}

.vip-icon {
  background-color: #ff6b81;
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
  font-weight: bold;
  font-size: 14px;
}

.user-details {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.details-section {
  background-color: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
}

.section-title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 20px;
  color: #333;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.info-item {
  display: flex;
  margin-bottom: 15px;
}

.info-label {
  width: 120px;
  font-weight: bold;
  color: #666;
}

.info-value {
  flex: 1;
  color: #333;
}

.action-buttons {
  display: flex;
  justify-content: center;
  margin-bottom: 50px;
}

.btn-logout {
  padding: 10px 20px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  background-color: #f5f5f5;
  color: #666;
  border: 1px solid #ddd;
}

.btn-logout:hover {
  background-color: #eee;
}

@media (max-width: 768px) {
  .profile-header {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
  
  .profile-avatar {
    margin-right: 0;
    margin-bottom: 20px;
  }
  
  .profile-meta {
    justify-content: center;
  }
  
  .vip-badge {
    position: static;
    margin-top: 15px;
  }
  
  .user-details {
    grid-template-columns: 1fr;
  }
}
</style> 